import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UploadCloud, FileSpreadsheet, CheckCircle2, Download, Loader2, ScanLine, ArrowRight, X, Save, Undo2 } from "lucide-react";

const NONE = "__none__";

export default function Upload() {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [presetName, setPresetName] = useState("");
  const navigate = useNavigate();

  const loadHistory = () => api.get("/catalog/uploads").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const reset = () => { setAnalysis(null); setMapping({}); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.(xlsx|xls|csv|pdf)$/i.test(file.name)) { toast.error("Please upload an .xlsx, .xls, .csv or .pdf file"); return; }
    setBusy(true); setResult(null); setAnalysis(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/catalog/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      setMapping(data.detected_mapping || {});
      if (data.source === "pdf-ocr") toast.success("Scanned PDF read with AI OCR — please review the mapping");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    if (!mapping.title) { toast.error("Map the Song Title column first"); return; }
    setImporting(true);
    try {
      const { data } = await api.post(`/catalog/import/${analysis.import_id}`, { mapping });
      setResult(data); setAnalysis(null);
      toast.success(`Imported ${data.songs_imported} songs, ${data.writers_imported} writers, ${data.publishers_imported} publishers`);
      loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const savePreset = async () => {
    if (!presetName.trim()) { toast.error("Name your preset first"); return; }
    try {
      await api.post("/catalog/mapping-presets", { name: presetName.trim(), columns: analysis.columns, mapping });
      toast.success(`Preset "${presetName.trim()}" saved — it'll auto-apply next time`);
      setPresetName("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const undoImport = async (id) => {
    try {
      const { data } = await api.post(`/catalog/uploads/${id}/undo`);
      toast.success(`Removed ${data.removed.songs} songs, ${data.removed.writers} writers, ${data.removed.publishers} publishers`);
      loadHistory();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="upload-page">
      <div className="mb-8">
        <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-zinc-500 mb-2">Catalog</h4>
        <h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Import Catalog</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Upload an Excel, CSV or PDF catalog (scanned PDFs supported via AI OCR). We auto-detect the columns and let you confirm the mapping before anything is imported.
        </p>
      </div>

      {!analysis && !result && (
        <div
          data-testid="dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer border-2 border-dashed rounded-xl p-14 text-center transition-colors ${dragging ? "border-primary bg-primary/5" : "border-zinc-700 hover:border-primary/50 bg-card"}`}
        >
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden" data-testid="file-input" onChange={(e) => handleFile(e.target.files?.[0])} />
          {busy ? <Loader2 className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" /> : (
            <div className="h-16 w-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-4"><UploadCloud className="h-7 w-7 text-primary" /></div>
          )}
          <p className="font-semibold text-lg">{busy ? "Analyzing file..." : "Drop your Excel, CSV or PDF file here"}</p>
          <p className="text-zinc-500 text-sm mt-1">or click to browse · Excel, CSV or PDF (incl. scanned)</p>
        </div>
      )}

      {analysis && (
        <div className="bg-card border border-border rounded-lg p-6 sm:p-8" data-testid="mapping-review">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-xl font-semibold">Review column mapping</h3>
                {analysis.source === "pdf-ocr" && <span className="text-[10px] px-2 py-0.5 rounded border border-cyan-400/40 bg-cyan-400/10 text-cyan-300 font-semibold flex items-center gap-1"><ScanLine className="h-3 w-3" />AI OCR</span>}
                {analysis.matched_preset && <span data-testid="matched-preset-badge" className="text-[10px] px-2 py-0.5 rounded border border-primary/40 bg-primary/10 text-primary font-semibold">Preset: {analysis.matched_preset}</span>}
              </div>
              <p className="text-sm text-zinc-500 mt-1">{analysis.filename} · {analysis.row_count} rows · {analysis.columns.length} columns detected. Confirm or fix each field, then import.</p>
            </div>
            <button onClick={reset} data-testid="cancel-import-btn" className="text-zinc-400 hover:text-zinc-100 p-1"><X className="h-5 w-5" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-8">
            {analysis.fields.map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-4">
                <label className="text-sm text-zinc-300 whitespace-nowrap">{label}{key === "title" && <span className="text-primary"> *</span>}</label>
                <Select value={mapping[key] || NONE} onValueChange={(v) => setMapping({ ...mapping, [key]: v === NONE ? null : v })}>
                  <SelectTrigger data-testid={`map-${key}`} className="bg-secondary border-border w-56"><SelectValue placeholder="— Not mapped —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— Not mapped —</SelectItem>
                    {analysis.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <h4 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Data preview</h4>
          <div className="overflow-x-auto border border-border rounded-lg mb-8">
            <table className="w-full text-sm" data-testid="sample-table">
              <thead><tr className="border-b border-border">{analysis.columns.map((c) => <th key={c} className="text-left p-2.5 font-medium text-zinc-400 whitespace-nowrap">{c}</th>)}</tr></thead>
              <tbody>
                {analysis.sample_rows.map((r, i) => (
                  <tr key={i} className="border-b border-border/50">{analysis.columns.map((c) => <td key={c} className="p-2.5 text-zinc-300 whitespace-nowrap max-w-[200px] truncate">{r[c]}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <input
              data-testid="preset-name-input"
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Preset name (e.g., My Distributor)"
              className="bg-secondary border border-border rounded-md px-3 py-2 text-sm w-full sm:w-64"
            />
            <Button onClick={savePreset} variant="outline" data-testid="save-preset-btn" className="border-border bg-secondary hover:bg-zinc-800">
              <Save className="h-4 w-4 mr-2" /> Save preset
            </Button>
          </div>

          <div className="flex gap-3">
            <Button onClick={runImport} disabled={importing} data-testid="confirm-import-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Import {analysis.row_count} rows <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
            <Button onClick={reset} variant="outline" className="border-border bg-secondary hover:bg-zinc-800">Cancel</Button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-card border border-border rounded-lg p-6 sm:p-8" data-testid="import-result">
          <div className="flex items-center gap-2 mb-6"><CheckCircle2 className="h-5 w-5 text-primary" /><h3 className="font-heading text-xl font-semibold">Import complete — {result.filename}</h3></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {[["Rows", result.rows], ["Songs", result.songs_imported], ["Writers", result.writers_imported], ["Publishers", result.publishers_imported]].map(([l, v]) => (
              <div key={l} className="bg-secondary border border-border rounded-lg p-4"><div className="font-mono text-3xl font-semibold">{v}</div><div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{l}</div></div>
            ))}
          </div>
          {result.preview?.length > 0 && (
            <>
              <h4 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Preview</h4>
              <div className="space-y-2">
                {result.preview.map((s, i) => (
                  <div key={i} className="flex items-center justify-between bg-secondary/50 border border-border rounded-md px-4 py-2.5">
                    <span className="font-semibold">{s.title}</span>
                    <span className="font-mono text-xs text-zinc-500">{(s.writer_splits?.length || 0)}W · {(s.publisher_splits?.length || 0)}P · {s.isrc || "no ISRC"}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="flex gap-3 mt-6">
            <Button onClick={() => navigate("/songs")} data-testid="view-catalog-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">View Catalog →</Button>
            <Button onClick={reset} variant="outline" data-testid="import-another-btn" className="border-border bg-secondary hover:bg-zinc-800">Import another</Button>
          </div>
        </div>
      )}

      <div className="mt-10">
        <h3 className="font-heading text-xl font-semibold mb-4">Upload History</h3>
        {history.length === 0 ? <p className="text-zinc-500 text-sm">No uploads yet.</p> : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} data-testid={`upload-history-${h.id}`} className="flex items-center justify-between bg-card border border-border rounded-lg px-5 py-4">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-5 w-5 text-primary" />
                  <div><div className="font-semibold text-sm">{h.filename}</div><div className="text-xs text-zinc-500 font-mono">{h.rows} rows · {h.songs_imported}S / {h.writers_imported}W / {h.publishers_imported}P</div></div>
                </div>
                <div className="flex items-center gap-3">
                  {h.undone
                    ? <span data-testid={`undone-${h.id}`} className="text-xs px-2 py-1 rounded border border-border bg-secondary text-zinc-500 font-semibold">Undone</span>
                    : <button onClick={() => undoImport(h.id)} data-testid={`undo-upload-${h.id}`} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-red-400 transition-colors"><Undo2 className="h-4 w-4" /> Undo</button>}
                  <a href={`${API}/catalog/uploads/${h.id}/download`} data-testid={`download-upload-${h.id}`} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-primary transition-colors"><Download className="h-4 w-4" /> File</a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
